import { api } from '@/lib/axios';
import type { 
  DashboardSummary, 
  DashboardFilters, 
  DashboardResponse,
  DashboardCategoryData,
  DashboardTypeData,
  DashboardSegmentData,
  DashboardRegionData,
  DashboardLocalityData
} from '../types/dashboard.types';

const STATS_BASE_URL = '/api/stats';

// Helper function to build query params
const buildQueryParams = (filters?: DashboardFilters): string => {
  const params = new URLSearchParams();
  
  // Always filter by active status (statusId = 1) by default
  const statusIds = filters?.status?.length ? filters.status : [1];
  params.set('status', statusIds.join(','));
  
  if (filters?.segment?.length) {
    params.set('segment', filters.segment.join(','));
  }
  
  if (filters?.region?.length) {
    params.set('region', filters.region.join(','));
  }

  if (filters?.source) {
    params.set('source', filters.source);
  }
  
  return `?${params.toString()}`;
};

// API functions
// Note: All endpoints automatically filter by statusId=1 (active status) by default
export const dashboardApi = {
  // Summary endpoint
  getSummary: async (filters?: DashboardFilters): Promise<DashboardSummary> => {
    const queryParams = buildQueryParams(filters);
    const response = await api.get(`${STATS_BASE_URL}/summary${queryParams}`);
    return response.data;
  },

  // Categories endpoint
  getByCategories: async (filters?: DashboardFilters): Promise<DashboardResponse<DashboardCategoryData>> => {
    const queryParams = buildQueryParams(filters);
    const response = await api.get(`${STATS_BASE_URL}/by-categories${queryParams}`);
    return response.data;
  },

  // Types endpoint
  getByTypes: async (filters?: DashboardFilters): Promise<DashboardResponse<DashboardTypeData>> => {
    const queryParams = buildQueryParams(filters);
    const response = await api.get(`${STATS_BASE_URL}/by-types${queryParams}`);
    return response.data;
  },

  // Segments endpoint
  getBySegments: async (filters?: DashboardFilters): Promise<DashboardResponse<DashboardSegmentData>> => {
    const queryParams = buildQueryParams(filters);
    const response = await api.get(`${STATS_BASE_URL}/by-segments${queryParams}`);
    return response.data;
  },

  // Regions endpoint
  getByRegions: async (filters?: DashboardFilters): Promise<DashboardResponse<DashboardRegionData>> => {
    const queryParams = buildQueryParams(filters);
    const response = await api.get(`${STATS_BASE_URL}/by-regions${queryParams}`);
    return response.data;
  },

  // Localities endpoint
  getByLocalities: async (filters?: DashboardFilters): Promise<DashboardResponse<DashboardLocalityData>> => {
    const queryParams = buildQueryParams(filters);
    const response = await api.get(`${STATS_BASE_URL}/by-localities${queryParams}`);
    return response.data;
  },
};